import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {setAddressEntryType} from '../v2/actions/address-actions';
import {getDeliveryOptions} from '../v2/actions/deliveryOptionActions';
import AddressDataValidators from '../v2/components/address/field-validation/AddressDataValidators';
import {isPostCodeExempt} from '../v2/selectors/DeliveryAddressSelectors/DeliveryAddressSelectors';
import cityRequiredForDeliveryCountry
from '../v2/selectors/DeliveryOptionsSelectors/cityRequiredForDeliveryCountrySelector';
import getAddressDefinitions from '../v2/selectors/getAddressDefinitions';
import {getNewAddress, getSelectedAddressID, getSelectedDeliveryCountry} from '../v2/selectors/selectors';
import V2Validation from '../v2/validation/v2-validation';

export function AutoFillListener(props) {
  const [shouldValidate, setShouldValidate] = useState(false);
  const [lastValidRequest, setLastValidRequest] = useState({country: '', postCode: '', townCity: ''});
  const [storedDeliveryRequest, setStoredDeliveryRequest] = useState({
    country: props.country, postCode: '', townCity: '', state: '',
  });
  const shouldMakeDeliveryRequest = (addressData) => {
    // doesnt resend request if the country and postcode are the same, avoids too many api calls
    if (!props.isPostCodeExempt && addressData.postCode === lastValidRequest.postCode
      && addressData.country === lastValidRequest.country && !props.requiresTownCity) {
      return false;
    }
  
    // doesnt resend request if the country and towncity are the same for postcode exemption, avoids too many api calls
    if (props.isPostCodeExempt && addressData.country === lastValidRequest.country
      && addressData.townCity === lastValidRequest.townCity) {
      return false;
    }
    
    // autofill has cleared form and switched country or the customer changed country and autofilled again
    if (addressData?.country && addressData.country !== storedDeliveryRequest.country) {
      if (!props.isPostCodeExempt && addressData?.postCode && addressData.postCode !== storedDeliveryRequest.postCode
        && addressData.postCode?.length > 3) {
        if (AddressDataValidators.validatePostCode(props.addressDefinitions, addressData.postCode)) {
          return true;
        }
        setShouldValidate(true);
        return false;
      }
    }
    
    // for postcode exempt countries or if requires townCity, searches for address by town
    if ((props.isPostCodeExempt && addressData.townCity) || props.requiresTownCity) {
      // no previous town city but townCity autofilled
      if (!storedDeliveryRequest.townCity && addressData.townCity?.length > 3) {
        if (AddressDataValidators.validateTownCity(props.addressDefinitions, addressData.townCity)) {
          return true;
        }
        setShouldValidate(true);
        return false;
      }
      
      // previous town city but townCity autofilled as length has jumped
      if (addressData.townCity?.length - storedDeliveryRequest.townCity?.length > 2) {
        if (AddressDataValidators.validateTownCity(props.addressDefinitions, addressData.townCity)) {
          return true;
        }
        setShouldValidate(true);
        return false;
      }
    }
    
    // country is the same but postcode has changed and is not exempt
    if (!props.isPostCodeExempt && addressData?.postCode && addressData.postCode !== storedDeliveryRequest.postCode) {
      // if autofill has cleared form and re-added a new one
      if (!storedDeliveryRequest.postCode && addressData.postCode?.length > 3) {
        if (AddressDataValidators.validatePostCode(props.addressDefinitions, addressData.postCode)) {
          return true;
        }
        setShouldValidate(true);
        return false;
      }
      
      // implies autofill if postcode length increases in one render by over 2 chars
      if (addressData.postCode?.length - storedDeliveryRequest.postCode?.length > 2) {
        if (AddressDataValidators.validatePostCode(props.addressDefinitions, addressData.postCode)) {
          return true;
        }
        setShouldValidate(true);
        return false;
      }
    }
    
    return false;
  };
  
  useEffect(() => {
    // stores saved address request - this will call delivery options on load
    if (props.addressId) {
      const savedAddress = props.addresses?.[props.addressId];
      setLastValidRequest({country: savedAddress.country, postCode: savedAddress.postCode});
    }
    
    if (shouldMakeDeliveryRequest(props.newAddress)) {
      // if entry type was loqate, we do not want to trigger delivery options - resetting type for next input
      if (props.isEntryTypeLoqate) {
        props.setAddressEntryType('STANDARD');
      } else {
        props.getDeliveryOptions();
        setLastValidRequest({
          country: props.newAddress.country,
          postCode: props.newAddress.postCode,
          townCity: props.newAddress.townCity,
        });
      }
    }
    
    setStoredDeliveryRequest(props.newAddress);
  }, [props.newAddress, props.addressId, props.addresses]);
  
  useEffect(() => {
    if (shouldValidate) {
      // only validates delivery form and should validate if autofill has been used with a invalid' address
      V2Validation.validate('AddressSection');
      setShouldValidate(false);
    }
  }, [shouldValidate]);
  
  return null;
}

const mapStateToProps = state => ({
  country: getSelectedDeliveryCountry(state),
  newAddress: getNewAddress(state),
  addressId: getSelectedAddressID(state),
  isPostCodeExempt: isPostCodeExempt(state),
  addressDefinitions: getAddressDefinitions(state),
  addresses: state.address?.deliveryAddressList?.addresses,
  requiresTownCity: cityRequiredForDeliveryCountry(state),
  isEntryTypeLoqate: state.address?.deliveryAddressList?.entryType === 'LOQATE',
});

export default connect(mapStateToProps, {getDeliveryOptions, setAddressEntryType})(AutoFillListener);
